import { useEffect, useState } from 'react';
import {
  Size, confidentialityGrantEndPath, fill, formatDate, formatDateOnly, grantAgreementGroup, personConfidentialityGrantsPath, t,
  type ConfidentialityGrantRow, type DateFormat, type GrantAgreementOption, type PersonConfidentialityView, type StringKey,
} from '@bms2/shared';
import { ConfirmationDialog, Select } from '../../components/controls';
import { LoadFailure } from '../../components/composites';
import { TileRowList, type TileItem } from '../../components/composites/Tile';
import { FormFrame } from '../../components/forms/FormFrame';
import { post, useApi } from './api';
import styles from './PersonConfidentialityPanel.module.css';

/**
 * 🔒 The Person page's Confidentiality Roles tile (piece 13; piece 8; T2352).
 * Each grant is a dated row of the Employments tile's shape: the role, the
 * backing agreement with its state beside it, who granted it and its span.
 * The open grants stand first and an ended one stays beneath as history. 🔒 An
 * open row carries End for an administrator, and + Grant Role is the tile
 * head's action, its window the record page's own, so a folded tile still opens
 * it; a grant written there is heard here through `announceConfidentiality`.
 */

const CHANGED = 'bms2:person-confidentiality';

/** Tells the tile to read again: the page's own window wrote a grant. */
export function announceConfidentiality(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(CHANGED));
}

function useHeard(reload: () => void): void {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.addEventListener(CHANGED, reload);
    return () => window.removeEventListener(CHANGED, reload);
  }, [reload]);
}

/** The agreement's state in words, Title Case as every pill reads. */
function stateWord(state: GrantAgreementOption['state']): string {
  return t(`confidentiality.state.${state.charAt(0).toLowerCase()}${state.slice(1)}` as StringKey);
}

/** The agreement as a row and the window name it: its dates, then its state. */
function agreementLine(agreement: GrantAgreementOption, dateFormat: DateFormat): string {
  const from = formatDateOnly(agreement.effectiveDate, dateFormat);
  return agreement.expiryDate
    ? fill(t('confidentiality.tile.agreementUntil'), { from, until: formatDateOnly(agreement.expiryDate, dateFormat) })
    : fill(t('confidentiality.tile.agreement'), { from });
}

export function PersonConfidentialityPanel({ personId, zone, dateFormat, apiBaseUrl = '' }: { personId: string; zone: string; dateFormat: DateFormat; apiBaseUrl?: string }) {
  const fetched = useApi<PersonConfidentialityView>(personConfidentialityGrantsPath(personId), apiBaseUrl);
  useHeard(fetched.reload);
  const [ending, setEnding] = useState<ConfidentialityGrantRow | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  if (fetched.state.kind === 'loading') return <p className={`${styles.quiet} ${styles.said}`}>{t('async.loading')}</p>;
  if (fetched.state.kind === 'failed') return <LoadFailure state={fetched.state} onRetry={fetched.reload} />;
  if (fetched.state.kind !== 'ready') return null;
  const view = fetched.state.data;
  const end = async (row: ConfidentialityGrantRow) => {
    setEnding(null);
    setBusy(row.id);
    setRefusal(null);
    const done = await post(confidentialityGrantEndPath(personId, row.id), {}, apiBaseUrl);
    setBusy(null);
    if (!done.ok) setRefusal(done.message);
    fetched.reload();
  };
  const items: TileItem[] = view.rows.map((row) => {
    const since = formatDate(row.grantedAt, zone, dateFormat);
    const open = row.endedAt === null;
    return {
      type: 'datedRole',
      role: row.roleName,
      organization: [agreementLine(row.agreement, dateFormat), fill(t('confidentiality.tile.grantedBy'), { name: row.grantedByName })].join(' · '),
      span: open ? fill(t('confidentiality.tile.since'), { from: since }) : fill(t('confidentiality.tile.span'), { from: since, until: formatDate(row.endedAt!, zone, dateFormat) }),
      // 🔒 The agreement's state beside the role on an open grant (piece 8), and Ended on a grant that has ended.
      pill: open ? { text: stateWord(row.agreement.state), group: grantAgreementGroup(row.agreement.state) } : { text: t('confidentiality.state.ended'), group: 'inactive' },
      button: open && view.writes && busy !== row.id ? { label: t('confidentiality.tile.end'), onAct: () => setEnding(row) } : null,
      data: { 'data-confidentiality-grant': row.id, 'data-grant-open': open ? 'true' : 'false' },
    };
  });
  return (
    <div className={styles.listPanel} data-person-confidentiality={personId} data-tile-custom-flush="">
      {refusal ? <p className={`${styles.refusal} ${styles.said}`} role="alert">{refusal}</p> : null}
      {items.length === 0 ? <p className={`${styles.quiet} ${styles.said}`}>{t('confidentiality.tile.none')}</p> : <TileRowList items={items} name="confidentialityRoles" />}
      {ending ? (
        <ConfirmationDialog
          open
          title={t('confidentiality.tile.endTitle')}
          consequence={{ body: t('confidentiality.tile.endConsequence'), recordNames: [ending.roleName] }}
          confirmLabel={t('confidentiality.tile.end')}
          onConfirm={() => void end(ending)}
          onCancel={() => setEnding(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * 🔒 + Grant Role (piece 13; T2352): the role, from the active ones the person
 * does not already hold open, and the person's own NDA, from their personal
 * agreements with each one's state. 🔨 Grant Role is live once a role is picked,
 * with or without an agreement, so the service's own refusal is what a person
 * reads when none is attached. Mounted only while open, so each opening reads
 * the roles and the agreements again.
 */
export function GrantRoleWindow({ personId, dateFormat, apiBaseUrl = '', onCancel, onSaved }: {
  personId: string;
  dateFormat: DateFormat;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const fetched = useApi<PersonConfidentialityView>(personConfidentialityGrantsPath(personId), apiBaseUrl);
  const [roleId, setRoleId] = useState('');
  const [agreementId, setAgreementId] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const view = fetched.state.kind === 'ready' ? fetched.state.data : null;
  const held = new Set((view?.rows ?? []).filter((row) => row.endedAt === null).map((row) => row.roleId));
  const roles = (view?.roles ?? []).filter((role) => !held.has(role.id));
  const agreements = view?.agreements ?? [];
  const save = async () => {
    setBusy(true);
    setRefusal(null);
    const answer = await post(personConfidentialityGrantsPath(personId), { confidentialityRoleId: roleId, ndaAgreementId: agreementId || null }, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setRefusal(answer.message);
    onSaved();
  };
  // 🔒 A login without the administrative capability sees why Grant Role will not go, before it picks a role (T3300).
  const refused = view !== null && !view.writes;
  const reason = refused ? t('confidentiality.window.needsAdmin') : view && roles.length === 0 ? t('confidentiality.window.noRoles') : t('confidentiality.window.pickRole');
  return (
    <FormFrame
      host="window"
      open
      title={t('confidentiality.window.title')}
      subtitle={t('confidentiality.window.subtitle')}
      onCancel={onCancel}
      dataForm="grant-confidentiality-role"
      typed={roleId !== '' || agreementId !== ''}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('confidentiality.window.act'), onAct: () => save(), disabled: busy || roleId === '' || refused, disabledReason: reason }}
    >
      <div data-grant-window={personId}>
        {fetched.state.kind === 'failed' ? <LoadFailure state={fetched.state} onRetry={fetched.reload} /> : null}
        <Select
          size={Size.Medium}
          label={t('confidentiality.window.role')}
          value={roleId}
          onChange={setRoleId}
          options={roles.map((role) => ({ value: role.id, label: role.name }))}
          placeholder={t('confidentiality.window.pickRole')}
          required
          dataAttribute={{ 'data-grant-role': 'true' }}
        />
        {refused ? <p className={styles.note} data-grant-needs-admin="true">{t('confidentiality.window.needsAdmin')}</p> : null}
        {view && roles.length === 0 ? <p className={styles.note}>{t('confidentiality.window.noRoles')}</p> : null}
        <Select
          size={Size.Large}
          label={t('confidentiality.window.agreement')}
          value={agreementId}
          onChange={setAgreementId}
          options={agreements.map((agreement) => ({ value: agreement.id, label: `${agreementLine(agreement, dateFormat)} · ${stateWord(agreement.state)}` }))}
          placeholder={t('confidentiality.window.noAgreement')}
          dataAttribute={{ 'data-grant-agreement': 'true' }}
        />
        {view && agreements.length === 0 ? <p className={styles.note}>{t('confidentiality.window.noAgreements')}</p> : null}
      </div>
    </FormFrame>
  );
}
